import {ActionList} from '@primer/react'
import {
  updateQueryParam,
  useUserDiffViewSettingsData,
  useWhitespacePreference,
} from '../page-data/payloads/diff-view-settings'
import {useUpdateUserDiffViewPreferenceMutation} from '../hooks/mutations/use-update-user-diff-view-preference-mutation'
import {useCallback} from 'react'

export type DiffLinePresentationTogglesProps = {
  /**
   * Default value for the hide whitespace setting.
   * If w=0 / w=1 is not present in the URL, this will be used.
   * If w=0 / w=1 is present, this will be ignored.
   * @default false
   */
  defaultHideWhitespace?: boolean
  lineSpacingPreferenceAvailable?: boolean
  onUpdateWhitespace?: (newVal: boolean) => void
  reloadOnChange?: () => void
}

export function DiffLinePresentationToggles({
  lineSpacingPreferenceAvailable = true,
  defaultHideWhitespace = false,
  onUpdateWhitespace,
  reloadOnChange,
}: DiffLinePresentationTogglesProps) {
  const {data} = useUserDiffViewSettingsData()
  const hideWhitespace = useWhitespacePreference(defaultHideWhitespace)
  const {mutate: updateUserDiffViewPreference} = useUpdateUserDiffViewPreferenceMutation({
    onSuccess: () => {},
    onError: () => {},
  })

  const updateWhitespacePreference = useCallback(() => {
    updateQueryParam('w', !hideWhitespace ? '1' : '0')
    onUpdateWhitespace?.(!hideWhitespace)

    if (reloadOnChange !== undefined) {
      reloadOnChange()
    }
  }, [hideWhitespace, onUpdateWhitespace, reloadOnChange])

  if (!data) return null

  return (
    <ActionList.Group aria-label="Format" selectionVariant="multiple" variant="subtle">
      <ActionList.Item selected={hideWhitespace} onSelect={updateWhitespacePreference}>
        Hide whitespace
      </ActionList.Item>
      {lineSpacingPreferenceAvailable && (
        <ActionList.Item
          selected={data.lineSpacing === 'compact'}
          onSelect={() =>
            updateUserDiffViewPreference({lineSpacing: data.lineSpacing === 'compact' ? 'relaxed' : 'compact'})
          }
        >
          Compact line height
        </ActionList.Item>
      )}
    </ActionList.Group>
  )
}

try{ DiffLinePresentationToggles.displayName ||= 'DiffLinePresentationToggles' } catch {}